/* Cart drawer – Trust badges autoplay
 * Custom element driving an autoplay crossfade on a sibling <scroll-carousel>.
 * Lifecycle-safe: disconnectedCallback clears timers, connectedCallback re-inits
 * on every cart-drawer re-render (cf. replaceChildren in CartDrawer).
 */
const FADE_DURATION = 250;
const RESUME_DELAY = 3000;

class TrustBadgesAutoplay extends HTMLElement {
  #intervalId = null;
  #fadeTimeoutId = null;
  #resumeTimeoutId = null;
  #carousel = null;
  #speedMs = 0;
  #onEnter = () => this.#pause();
  #onLeave = () => this.#start();
  #onInteractStart = () => this.#pause();
  #onInteractEnd = () => this.#scheduleResume();

  async connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targetId = this.dataset.target;
    const speed = parseInt(this.dataset.cycleSpeed, 10);
    if (!targetId || !speed) return;

    this.#speedMs = speed * 1000;

    await customElements.whenDefined('scroll-carousel');
    // Element may have been disconnected while awaiting the upgrade.
    if (!this.isConnected) return;

    const carousel = document.getElementById(targetId);
    if (!carousel || typeof carousel.select !== 'function') return;

    this.#carousel = carousel;
    carousel.dataset.autoplayFade = 'false';
    carousel.addEventListener('mouseenter', this.#onEnter);
    carousel.addEventListener('mouseleave', this.#onLeave);
    carousel.addEventListener('pointerdown', this.#onInteractStart);
    carousel.addEventListener('pointerup', this.#onInteractEnd);
    carousel.addEventListener('pointercancel', this.#onInteractEnd);

    this.#start();
  }

  disconnectedCallback() {
    this.#pause();
    if (this.#carousel) {
      this.#carousel.removeEventListener('mouseenter', this.#onEnter);
      this.#carousel.removeEventListener('mouseleave', this.#onLeave);
      this.#carousel.removeEventListener('pointerdown', this.#onInteractStart);
      this.#carousel.removeEventListener('pointerup', this.#onInteractEnd);
      this.#carousel.removeEventListener('pointercancel', this.#onInteractEnd);
      this.#carousel = null;
    }
  }

  #start() {
    this.#pause();
    this.#intervalId = setInterval(() => this.#advance(), this.#speedMs);
  }

  #pause() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
    if (this.#fadeTimeoutId) {
      clearTimeout(this.#fadeTimeoutId);
      this.#fadeTimeoutId = null;
    }
    if (this.#resumeTimeoutId) {
      clearTimeout(this.#resumeTimeoutId);
      this.#resumeTimeoutId = null;
    }
    if (this.#carousel) {
      this.#carousel.dataset.autoplayFade = 'false';
    }
  }

  #scheduleResume() {
    if (this.#resumeTimeoutId) clearTimeout(this.#resumeTimeoutId);
    this.#resumeTimeoutId = setTimeout(() => {
      this.#resumeTimeoutId = null;
      if (this.#carousel?.isConnected) this.#start();
    }, RESUME_DELAY);
  }

  #advance() {
    const carousel = this.#carousel;
    if (!carousel?.isConnected || !carousel.cells?.length) return;

    const next = (carousel.selectedIndex + 1) % carousel.cells.length;
    carousel.dataset.autoplayFade = 'true';
    this.#fadeTimeoutId = setTimeout(() => {
      this.#fadeTimeoutId = null;
      if (!carousel.isConnected) return;
      carousel.select(next, { instant: true });
      carousel.dataset.autoplayFade = 'false';
    }, FADE_DURATION);
  }
}

if (!window.customElements.get('trust-badges-autoplay')) {
  window.customElements.define('trust-badges-autoplay', TrustBadgesAutoplay);
}
